import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Banner, Button, Card, FileDrop, formatMoney, PageHeader, Pill, Tabs, type Tone } from '@elysian/ui';
import { ATTACHMENT_ACCEPT } from '../../data/files';
import type { Contact, InboxItem, InboxStatus } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { InboxAddress } from './InboxAddress';

export const INBOX_STATE: Record<InboxStatus, { tone: Tone; label: string }> = {
  received: { tone: 'info', label: 'Waiting for virus scan' },
  processing: { tone: 'info', label: 'Being read' },
  needs_supplier: { tone: 'warn', label: 'Who is it from?' },
  needs_review: { tone: 'warn', label: 'Needs a check' },
  ready: { tone: 'ok', label: 'Ready to approve' },
  approved: { tone: 'ok', label: 'Approved' },
  rejected: { tone: 'draft', label: 'Rejected' },
  failed: { tone: 'danger', label: 'Couldn’t be read' },
};

/** The state shown for an item, telling apart the steps hidden inside "received". */
export function inboxState(item: InboxItem): { tone: Tone; label: string } {
  if (item.status === 'received') {
    if (item.scan_status === 'pending') return { tone: 'info', label: 'Being checked for viruses' };
    return { tone: item.retry_at ? 'warn' : 'info', label: item.retry_at ? 'Waiting to be read' : 'Queued to be read' };
  }
  if (item.status === 'failed' && item.scan_status === 'infected') return { tone: 'danger', label: 'Failed the virus scan' };
  return INBOX_STATE[item.status];
}

/** The value DocuLens read for a field, as text. */
export function readValue(item: InboxItem, field: string): string | undefined {
  const v = item.fields?.[field]?.value;
  return v === undefined || v === null ? undefined : String(v);
}

export function supplierLabel(item: InboxItem, suppliers: Contact[]): string {
  return suppliers.find((s) => s.id === item.matched_contact_id)?.name ?? readValue(item, 'supplier_name') ?? item.email_from ?? (item.source === 'upload' ? 'Uploaded here' : 'Unknown sender');
}

const received = (s: string) =>
  new Intl.DateTimeFormat('en-IE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Dublin' }).format(new Date(s));

type View = 'todo' | 'approved' | 'rejected';
interface Upload { name: string; state: 'sending' | 'added' | 'already' | 'error'; note?: string }

export function BillInbox() {
  const { source } = useSession();
  const navigate = useNavigate();
  const [viewing, setViewing] = useState<View>('todo');
  const [version, setVersion] = useState(0);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const data = useAsync(async () => {
    const [list, suppliers] = await Promise.all([
      source.inbox(viewing === 'todo' ? undefined : viewing), source.contacts('supplier'),
    ]);
    return { ...list, suppliers };
  }, [source, viewing, version], { keep: true });

  // One at a time, so a big batch doesn't swamp a slow connection; each shows how it went.
  async function upload(files: File[]) {
    const start = uploads.length;
    setUploads((u) => [...u, ...files.map((f): Upload => ({ name: f.name, state: 'sending' }))]);
    for (const [i, f] of files.entries()) {
      let next: Upload;
      try {
        const r = await source.uploadAttachment(f, { to_inbox: true });
        next = r.is_new ? { name: f.name, state: 'added' } : { name: f.name, state: 'already', note: r.inbox_id ? 'already in the inbox' : 'already uploaded' };
      } catch (e) {
        next = { name: f.name, state: 'error', note: e instanceof Error ? e.message : String(e) };
      }
      setUploads((u) => u.map((x, j) => (j === start + i ? next : x)));
    }
    setViewing('todo');
    setVersion((v) => v + 1);
  }

  const usage = data.state === 'ready' ? data.data.usage : null;
  const month = usage ? new Intl.DateTimeFormat('en-IE', { month: 'long' }).format(new Date(`${usage.month}-15T12:00:00Z`)) : '';
  const items = data.state === 'ready' ? data.data.items : [];
  const waiting = items.filter((i) => i.status === 'needs_supplier' || i.status === 'needs_review' || i.status === 'ready').length;
  const toast = useToast((t) => t.show);
  // While bills are being scanned or read, check every few seconds so they move on by themselves.
  const busy = items.some((i) => i.status === 'received' || i.status === 'processing');
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(() => setVersion((v) => v + 1), 4000);
    return () => clearInterval(t);
  }, [busy]);

  async function retry(item: InboxItem) {
    try {
      await source.retryInbox(item.id);
      toast(`${item.file_name} will be tried again`);
      setVersion((v) => v + 1);
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <>
      <PageHeader
        title="Bill inbox"
        subtitle={viewing === 'todo' && data.state === 'ready'
          ? `${waiting} ${waiting === 1 ? 'bill' : 'bills'} to deal with. Forward supplier bills by email, upload them here, or snap them on your phone; they’re read for you.`
          : 'Supplier bills, read and waiting for you'}
      />
      {data.state === 'error' && <Banner>Couldn’t load the inbox: {data.error.message}</Banner>}
      <div className="el-inbox-top">
        {usage && (
          <div className="el-usage" aria-label="Bills read this month">
            <div className="el-spread">
              <span>{usage.used} of {usage.allowance} bills read in {month}</span>
              <span className="el-hint">{Math.max(usage.allowance - usage.used, 0)} left</span>
            </div>
            <div className="el-meter" role="presentation"><i style={{ width: `${Math.min(100, (usage.used / Math.max(usage.allowance, 1)) * 100)}%` }} /></div>
          </div>
        )}
        {/* #22: the address suppliers send bills to, beside how many have been read this month */}
        <InboxAddress />
      </div>
      <div style={{ marginBottom: 16 }}>
        <FileDrop multiple label="Upload bills" accept={ATTACHMENT_ACCEPT}
          hint="or drop PDFs and photos of bills here (up to 25 MB each)" onFiles={(f) => void upload(f)} />
        {uploads.length > 0 && (
          <ul className="el-uploads" aria-label="Uploads">
            {uploads.map((u, i) => (
              <li key={i} data-state={u.state}>
                {u.name}: {u.state === 'sending' ? 'uploading…' : u.state === 'added' ? 'added; it’s checked for viruses, then read' : u.note}
              </li>
            ))}
          </ul>
        )}
      </div>
      <Card>
        <Tabs<View> label="Show bills" value={viewing} onChange={setViewing}
          tabs={[{ key: 'todo', label: 'To deal with' }, { key: 'approved', label: 'Approved' }, { key: 'rejected', label: 'Rejected' }]} />
        <div className="el-table-wrap">
          <table className="el-table" aria-label="Bills">
            <thead>
              <tr><th scope="col">Received</th><th scope="col">From</th><th scope="col">Bill</th><th scope="col">Status</th><th scope="col" className="el-num">Total</th></tr>
            </thead>
            <tbody>
              {data.state === 'loading' && <tr><td colSpan={5}><div className="el-skeleton" style={{ height: 18 }} aria-label="Loading bills" /></td></tr>}
              {data.state === 'ready' && items.length === 0 && (
                <tr><td colSpan={5} className="el-muted">{viewing === 'todo' ? 'Nothing waiting. New bills appear here as they arrive.' : 'None yet.'}</td></tr>
              )}
              {data.state === 'ready' && items.map((i) => {
                const s = inboxState(i);
                const gross = readValue(i, 'gross');
                const href = `/inbox/${i.id}`;
                return (
                  <tr key={i.id} data-href={href} onClick={(e) => (e.target as HTMLElement).closest('a') || navigate(href)}>
                    <td style={{ whiteSpace: 'nowrap' }}>{received(i.received_at)}</td>
                    <td><Link className="el-row-link" style={{ whiteSpace: 'normal' }} to={href}>{supplierLabel(i, data.data.suppliers)}</Link></td>
                    <td>
                      <div>{readValue(i, 'invoice_number') ?? '—'}</div>
                      <div className="el-hint">{i.file_name}</div>
                    </td>
                    <td>
                      <Pill tone={s.tone}>{s.label}</Pill>
                      {(i.warnings?.length ?? 0) > 0 && <div className="el-hint">{i.warnings![0]}</div>}
                      {i.error && <div className="el-hint">{i.error}</div>}
                      {i.can_retry && (
                        <Button size="sm" variant="ghost" aria-label={`Try ${i.file_name} again`}
                          onClick={(e) => { e.stopPropagation(); void retry(i); }}>Try again</Button>
                      )}
                    </td>
                    <td className="el-num">{gross ? formatMoney(gross, readValue(i, 'currency') ?? 'EUR') : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}
